import { Chip } from "@/components/ui/baseUi/chip"
import { ChipGroup } from "@/components/ui/baseUi/chip-groupe"
import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { InputGroup, InputGroupAddon } from "@/components/ui/baseUi/input-group"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { TemplateTable } from "@/modules/template/components/templateTable"
import { useTemplateCategoriesSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import {
  useTemplatePageActions,
  useTemplatePageSearchTerm,
  useTemplatePageTemplateTypeFilter,
  useTemplatePreviousProgramId,
} from "@/modules/template/stores/useTemplatePageStore"
import { TemplateTypeFilter } from "@/modules/template/types/templateType"
import { ArrowLeftIcon, CircleQuestionMarkIcon, PencilIcon, SearchIcon, XIcon } from "lucide-react"

type TemplatePageProps = {
  className?: string
} & React.ComponentProps<"div">

export function TemplatePage({ className, ...props }: TemplatePageProps) {
  const { navigateToTemplateCategories } = useTemplateCategoriesSidebar()
  const searchTerm = useTemplatePageSearchTerm()
  const templateTypeFilter = useTemplatePageTemplateTypeFilter()
  const { setSearchTerm, setTemplateTypeFilter } = useTemplatePageActions()
  const mySpaceNavigate = useMySpaceNavigate()
  const previousProgramId = useTemplatePreviousProgramId()

  const defaultValues = {
    searchTerm: searchTerm,
  }
  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
  })

  return (
    <div className={cn("flex flex-1 flex-col px-6 py-8", className)} {...props}>
      <div className="flex flex-1 flex-col gap-6">
        <div className="flex flex-col">
          <Button
            variant="underline"
            size="lg"
            className="w-fit"
            onClick={() => {
              if (previousProgramId) {
                mySpaceNavigate(Path.MySpace.ProgramDetails(previousProgramId))
              } else {
                mySpaceNavigate(Path.MySpace.Training)
              }
            }}
          >
            <ArrowLeftIcon size={12} /> {word("global.return")}
          </Button>
          <Text fontStyle="HL">{word("template.page.title")}</Text>
        </div>
        <div className="flex flex-col gap-3">
          <form.AppField
            name="searchTerm"
            validators={{
              onChangeAsyncDebounceMs: 350,
              onChangeAsync: async ({ value }) => {
                await new Promise((resolve) => setTimeout(resolve, 0))
                setSearchTerm(value)
              },
            }}
          >
            {(field) => (
              <field.Field className="max-w-94">
                <field.Content>
                  <InputGroup>
                    <field.InputGroupInput name="searchTerm" placeholder={word("template.page.search.placeholder")} />
                    <InputGroupAddon align="inline-start">
                      <SearchIcon />
                    </InputGroupAddon>
                    {field.state.value && (
                      <InputGroupAddon align="inline-end">
                        <XIcon
                          className="size-4 cursor-pointer"
                          onClick={() => {
                            field.handleChange("")
                          }}
                        />
                      </InputGroupAddon>
                    )}
                  </InputGroup>

                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <div className="flex flex-row justify-between">
            <div className="flex flex-row gap-2">
              <ChipGroup spacing={2} variant="emphasisHigh" defaultValue={[templateTypeFilter]}>
                <Chip
                  value={TemplateTypeFilter.EXERCISE}
                  variant="emphasisHigh"
                  size="md"
                  aria-pressed={templateTypeFilter === TemplateTypeFilter.EXERCISE}
                  onClick={() => {
                    setTemplateTypeFilter(TemplateTypeFilter.EXERCISE)
                  }}
                >
                  {word("global.chips.text.block", { count: 2 })}
                </Chip>
                <Chip
                  value={TemplateTypeFilter.SESSION}
                  variant="emphasisHigh"
                  size="md"
                  aria-pressed={templateTypeFilter === TemplateTypeFilter.SESSION}
                  onClick={() => {
                    setTemplateTypeFilter(TemplateTypeFilter.SESSION)
                  }}
                >
                  {word("global.chips.text.session", { count: 2 })}
                </Chip>
                <Chip
                  value={TemplateTypeFilter.WEEK}
                  variant="emphasisHigh"
                  size="md"
                  aria-pressed={templateTypeFilter === TemplateTypeFilter.WEEK}
                  onClick={() => {
                    setTemplateTypeFilter(TemplateTypeFilter.WEEK)
                  }}
                >
                  {word("global.chips.text.week", { count: 1 })}
                </Chip>
              </ChipGroup>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button variant="ghost" size="iconMd">
                      <CircleQuestionMarkIcon size={12} />
                    </Button>
                  }
                />
                <TooltipContent className="max-w-md" side="right" align="start">
                  <div className="space-y-3 text-sm">
                    <Text fontStyle="BM">{word("template.page.tooltip.text.typesOfTemplates")}</Text>
                    <ul className="ml-4 list-inside list-disc space-y-1">
                      <li>
                        <Text fontStyle="BM">{word("template.page.tooltip.text.weekly")}</Text>
                      </li>
                      <li>
                        <Text fontStyle="BM">{word("template.page.tooltip.text.sessionTemplate")}</Text>
                      </li>
                      <li>
                        <Text fontStyle="BM">{word("template.page.tooltip.text.blockTemplate")}</Text>
                      </li>
                    </ul>
                    <Text fontStyle="BM" className="mt-6 mb-2 flex font-bold">
                      {word("template.page.tooltip.text.howToAddTemplate")}
                    </Text>
                    <ul className="flex list-none flex-col gap-4">
                      <li className="flex flex-col gap-1">
                        <Text fontStyle="BM" className="italic">
                          {word("global.chips.text.block", { count: 1 })}
                        </Text>
                        <Text fontStyle="BM" className="text-wrap">
                          {word("template.page.tooltip.text.block")}
                        </Text>
                      </li>
                      <li className="flex flex-col gap-1">
                        <Text fontStyle="BM" className="italic">
                          {word("global.chips.text.session", { count: 1 })}
                        </Text>
                        <Text fontStyle="BM" className="text-wrap">
                          {word("template.page.tooltip.text.session")}
                        </Text>
                      </li>
                      <li className="flex flex-col gap-1">
                        <Text fontStyle="BM" className="italic">
                          {word("global.chips.text.week", { count: 1 })}
                        </Text>
                        <Text fontStyle="BM" className="text-wrap">
                          {word("template.page.tooltip.text.week")}
                        </Text>
                      </li>
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                navigateToTemplateCategories()
              }}
            >
              <PencilIcon size={16} />
              {word("global.category", { count: 2 })}
            </Button>
          </div>
        </div>
        <TemplateTable />
      </div>
    </div>
  )
}
